import {Component, Input, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {IntroducesService} from 'src/app/routes/store/introduces/introduces.service';
import {TemplateService} from 'src/app/routes/home/template.service';
import {GlobalCacheService} from 'src/app/shared/service/global-cache.service';
import {ModalConfigService} from 'src/app/shared/utils/modal-config.service';

export interface ContactusFormItem {
  key?: string;
  title?: string;
  type?: string;
  value?: any;
  data?: Array<{label: string, value: any}>;
  options?: {
    required?: boolean;
    formPlaceholder?: string;
    multiple?: boolean;
    fileType?: string;
    fileSize?: number;
    fileCount?: number;
  };
}

export interface ContactusEventData {
  jumpType?: string;
  name?: string;
  contactType?: 'sendEmail' | 'message' | string;
  emailTemplete?: string;
  emailTitle?: string;
  formIntroduction?: string;
  formInformedConsent?: string;
  successText?: string;
  failureText?: string;
  contactusForms?: ContactusFormItem[];
  [key: string]: any;
}

const VISIT_LOG_TOP_LEVEL_KEYS = ['name', 'mobile', 'address', 'attachment'];

function hasOwn(target: any, key: string): boolean {
  return !!target && Object.prototype.hasOwnProperty.call(target, key);
}

function cloneValue(value: any): any {
  if (Array.isArray(value)) {
    return value.map(item => cloneValue(item));
  }
  if (value && typeof value === 'object') {
    const result: any = {};
    Object.keys(value).forEach(key => result[key] = cloneValue(value[key]));
    return result;
  }
  return value;
}

@Component({
  selector: 'app-contactus-modal',
  templateUrl: './contactus-modal.component.html',
  styleUrls: ['./contactus-modal.component.scss']
})
export class ContactusModalComponent implements OnInit {
  @Input() contactData: ContactusEventData = {
    jumpType: 'contactus',
    contactType: 'message',
    contactusForms: []
  };

  public forms: ContactusFormItem[] = [];
  public validateForm: FormGroup;
  public loading = false;
  public consentAccepted = false;

  constructor(
    private fb: FormBuilder,
    private introducesService: IntroducesService,
    private templateService: TemplateService,
    private globalCacheService: GlobalCacheService,
    private modalConfigService: ModalConfigService,
  ) {
    this.validateForm = this.fb.group({});
  }

  ngOnInit(): void {
    const source = this.contactData || {};
    this.contactData = cloneValue(source);
    if (!this.contactData.contactType) {
      this.contactData.contactType = 'message';
    }
    this.forms = Array.isArray(this.contactData.contactusForms)
      ? this.contactData.contactusForms
        .filter(item => !!item && !!String(item.key || '').trim())
        .map(item => this.cloneFormItem(item))
      : [];
    this.contactData.contactusForms = this.forms;
    this.consentAccepted = false;
    this.createForm(this.forms);
  }

  createForm(forms: ContactusFormItem[]): void {
    const controls: any = {};
    (forms || []).forEach(item => {
      const key = item && item.key ? String(item.key).trim() : '';
      if (!key) {
        return;
      }
      const validators = item.options && item.options.required === true ? [Validators.required] : [];
      controls[key] = this.fb.control(this.initialValue(item), validators);
    });
    this.validateForm = this.fb.group(controls);
  }

  public getFormItemLabel(item: ContactusFormItem): string {
    const title = item && item.title !== undefined && item.title !== null ? String(item.title).trim() : '';
    const key = item && item.key !== undefined && item.key !== null ? String(item.key).trim() : '';
    return title || key;
  }

  async submit(): Promise<boolean> {
    Object.keys(this.validateForm.controls).forEach(key => {
      this.validateForm.controls[key].markAsDirty();
      this.validateForm.controls[key].updateValueAndValidity();
    });
    if (this.validateForm.invalid || this.loading || !this.forms.length ||
      (this.contactData.formInformedConsent && !this.consentAccepted)) {
      return false;
    }

    this.loading = true;
    try {
      const values = this.validateForm.value;
      if (this.contactData.contactType === 'message') {
        await this.templateService.addVisitLog(this.buildVisitLogPayload(values));
      } else {
        const payload = this.introducesService.disposeSendEmailData(this.contactData, this.forms, values);
        await this.introducesService.sendEmail(payload);
      }
      const successText = hasOwn(this.contactData, 'successText') ? this.contactData.successText : '发送成功';
      this.modalConfigService.successMsg(successText);
      this.validateForm.reset();
      return true;
    } catch (error) {
      const failureText = hasOwn(this.contactData, 'failureText') ? this.contactData.failureText : '发送失败';
      this.modalConfigService.errMsg(failureText);
      return false;
    } finally {
      this.loading = false;
    }
  }

  private initialValue(item: ContactusFormItem): any {
    if (item && item.type === 'upload') {
      return hasOwn(item, 'value') && Array.isArray(item.value) ? cloneValue(item.value) : [];
    }
    if (hasOwn(item, 'value')) {
      return cloneValue(item.value);
    }
    return item && (item.type === 'select' || item.type === 'radio') ? null : '';
  }

  private cloneFormItem(item: ContactusFormItem): ContactusFormItem {
    const result: ContactusFormItem = {
      key: item.key,
      title: item.title,
      type: item.type,
      value: hasOwn(item, 'value') ? cloneValue(item.value) : undefined,
      data: Array.isArray(item.data) ? item.data.map(option => ({
        label: option && hasOwn(option, 'label') ? option.label : '',
        value: option && hasOwn(option, 'value') ? cloneValue(option.value) : ''
      })) : [],
      options: item.options ? cloneValue(item.options) : {}
    };
    return result;
  }

  private buildVisitLogPayload(values: any): any {
    const merchant = this.globalCacheService && this.globalCacheService.merchant;
    const payload: any = {
      merchantId: merchant && merchant.id !== undefined ? merchant.id : '',
      name: '',
      mobile: '',
      address: '',
      content: '{}',
      attachment: '',
    };
    const dynamicFields: any[] = [];

    this.forms.forEach(item => {
      const key = item && item.key ? String(item.key).trim() : '';
      if (!key) {
        return;
      }
      const value = values && hasOwn(values, key) ? values[key] : '';
      if (VISIT_LOG_TOP_LEVEL_KEYS.indexOf(key) !== -1) {
        payload[key] = this.toTopLevelValue(value);
      } else {
        dynamicFields.push({
          key,
          label: this.getFormItemLabel({title: item.title, key}),
          value: this.toSerializableValue(value)
        });
      }
    });

    if (dynamicFields.length) {
      payload.content = JSON.stringify(dynamicFields);
    }
    return payload;
  }

  private toTopLevelValue(value: any): any {
    const serializable = this.toSerializableValue(value);
    if (Array.isArray(serializable)) {
      return serializable.join(',');
    }
    if (serializable && typeof serializable === 'object') {
      return JSON.stringify(serializable);
    }
    return serializable;
  }

  private toSerializableValue(value: any): any {
    if (value === undefined || value === null) {
      return '';
    }
    if (Array.isArray(value)) {
      return value.map(item => this.toSerializableValue(item));
    }
    if (value && typeof value === 'object') {
      if (hasOwn(value, 'url')) {
        return value.url;
      }
      if (hasOwn(value, 'name') && (hasOwn(value, 'uid') || hasOwn(value, 'type') || hasOwn(value, 'originFileObj'))) {
        return value.name;
      }
      return Object.keys(value).reduce((result, key) => {
        result[key] = this.toSerializableValue(value[key]);
        return result;
      }, {});
    }
    return value;
  }
}
